import React from "react";
import { Link } from "react-router-dom";
import PolicyPage from "./PolicyPage";
import "../css/returns.scss";

const sections = [
  {
    id: "cancellations",
    title: "Order cancellations",
    content: (
      <>
        <p>
          You may request a full cancellation before an order is dispatched or
          prepared as a custom item. Contact us immediately with your order
          number. Once a parcel has entered the carrier network, it can no
          longer be cancelled and must follow the return process below.
        </p>
        <p>
          If we can stop the order in time, the refund will be sent to the
          original payment method. We cannot guarantee a cancellation request
          made after packing has begun.
        </p>
      </>
    ),
  },
  {
    id: "return-window",
    title: "14-day return window",
    content: (
      <>
        <p>
          You may request a return within 14 days after receiving an eligible
          product. Contact us before sending anything back so that we can confirm
          eligibility and provide the correct return instructions and address.
        </p>
        <p className="policy-note">
          Sending a parcel to the manufacturer or an address not supplied by our
          support team may delay or prevent the return from being processed.
        </p>
      </>
    ),
  },
  {
    id: "return-condition",
    title: "Return condition",
    content: (
      <>
        <p>An eligible item must be:</p>
        <ul>
          <li>Unused, undamaged and in the condition in which it arrived</li>
          <li>Returned with its original packaging, accessories and manuals</li>
          <li>Accompanied by the order number or other proof of purchase</li>
          <li>Securely packed to avoid damage during return transit</li>
        </ul>
        <p>
          You may inspect a product as you would in a retail store, but handling
          beyond that may reduce a refund where permitted by law.
        </p>
      </>
    ),
  },
  {
    id: "non-returnable",
    title: "Non-returnable items",
    content: (
      <>
        <p>Unless faulty or required by applicable consumer law, we cannot accept:</p>
        <ul>
          <li>Gift cards, downloadable products or final-sale items</li>
          <li>Personalized, customized or made-to-order products</li>
          <li>Opened hygiene-sensitive items such as used in-ear ear tips</li>
          <li>Products damaged through misuse, accident or unauthorized repair</li>
          <li>Items missing serial labels, accessories or substantial packaging</li>
        </ul>
      </>
    ),
  },
  {
    id: "start-return",
    title: "How to start a return",
    content: (
      <ol>
        <li>
          Email <a href="mailto:support@dzcp896.com">support@dzcp896.com</a> or
          use the <Link to="/contact">contact page</Link> within 14 days of delivery.
        </li>
        <li>
          Include your order number, the item to be returned and the reason for
          the request. Add clear photos when the item is faulty or damaged.
        </li>
        <li>
          Wait for approval and the return address before shipping the parcel.
        </li>
        <li>
          Use protective packaging and a trackable service, then keep the
          shipping receipt until the return is completed.
        </li>
      </ol>
    ),
  },
  {
    id: "return-shipping",
    title: "Return shipping and fees",
    content: (
      <>
        <p>
          We do not charge a restocking fee. For a change-of-mind return, you may
          be responsible for one return-shipping charge. Original express or
          optional delivery charges are generally not refundable.
        </p>
        <p>
          If we confirm that the product was faulty, damaged on arrival or
          different from the item ordered, we will provide an appropriate return
          solution and cover reasonable return costs as required by law.
        </p>
      </>
    ),
  },
  {
    id: "inspection-refunds",
    title: "Inspection and refunds",
    content: (
      <>
        <p>
          We will inspect the returned item after it reaches the approved return
          location and email you with the outcome. Approved refunds are issued
          to the original payment method. We cannot redirect a refund to another
          person, card or payment account.
        </p>
        <p>
          Once issued, a refund may take several business days to appear,
          depending on your bank or payment provider. Shipping time to the return
          location and inspection time are separate from this banking period.
        </p>
      </>
    ),
  },
  {
    id: "late-refunds",
    title: "Late or missing refunds",
    content: (
      <>
        <p>
          If we have confirmed a refund but it is not visible, first check the
          original payment account and then contact the card issuer or bank.
          Pending credits may take time to be formally posted.
        </p>
        <p>
          If the provider cannot locate the credit after its normal processing
          period, contact us with the order number and refund confirmation so we
          can review the transaction.
        </p>
      </>
    ),
  },
  {
    id: "damaged-exchanges",
    title: "Damaged items and exchanges",
    content: (
      <>
        <p>
          Report a damaged, defective or incorrect item promptly after delivery
          and keep its packaging. Photos of the product, parcel and shipping
          label help us resolve the issue more quickly.
        </p>
        <p>
          Direct exchanges depend on stock availability. If an exchange cannot
          be arranged, we may ask you to return the original item for a refund
          and place a new order. This policy does not limit any mandatory
          consumer guarantee or remedy available to you.
        </p>
      </>
    ),
  },
];

function Returns() {
  return (
    <PolicyPage
      className="returns"
      title="Returns & Refunds"
      summary="Changed your mind or received a product that needs attention? Review our 14-day return process and refund guidance."
      sections={sections}
    />
  );
}

export default Returns;
